import { useState, useCallback } from "react";
import { RotateCw, Upload, Download, Trash2, FileText, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  { question: "Is rotating PDF pages free?", answer: "Yes, completely free with no signup required." },
  { question: "Can I rotate specific pages?", answer: "Yes, click on individual pages to rotate them 90° at a time." },
  { question: "Will rotating affect quality?", answer: "No, the original quality is preserved." },
  { question: "Can I rotate all pages at once?", answer: "Yes, use the 'Rotate All' button to rotate every page simultaneously." },
  { question: "Are my files secure?", answer: "Yes, files auto-delete after 30 minutes." }
];

const PdfRotatePage = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);
  const [rotations, setRotations] = useState({});

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPages(response.data.pages);
        setRotations({});
      }
    } catch (error) {
      toast.error("Failed to load preview");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const rotatePage = (pageNum, angle) => {
    setRotations((prev) => {
      const current = prev[pageNum] || 0;
      const newAngle = (current + angle + 360) % 360;
      if (newAngle === 0) {
        const { [pageNum]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [pageNum]: newAngle };
    });
  };

  const rotateAll = (angle) => {
    const newRotations = {};
    pages.forEach((page) => {
      const current = rotations[page.page] || 0;
      const newAngle = (current + angle + 360) % 360;
      if (newAngle !== 0) {
        newRotations[page.page] = newAngle;
      }
    });
    setRotations(newRotations);
  };

  const handleRotate = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (Object.keys(rotations).length === 0) {
      toast.error("No rotations to apply");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("rotations", JSON.stringify(rotations));

    try {
      const response = await axios.post(`${API}/pdf-rotate`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rotation failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        await downloadFile(`${API}/download/${result.files[0].id}`, result.files[0].name || "rotated.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPages([]);
    setRotations({});
  };

  return (
    <>
      <SEOHead
        title="Rotate PDF Pages Free Online | Fix Orientation"
        description="Rotate PDF pages to fix orientation. Rotate individual pages or all at once. Free online PDF rotator. No signup required."
        canonicalPath="/pdf-rotate"
        keywords="rotate PDF, PDF rotator free, fix PDF orientation, turn PDF pages, rotate PDF online"
      />
      <div className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
                <RotateCw className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Rotate PDF Pages Free Online
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Fix page orientation in your PDF. Rotate individual pages or all at once. 
              No signup required.
            </p>
          </div>

          {/* Upload Area */}
          {!result && pages.length === 0 && (
            <div className="space-y-6 animate-fade-in-up animate-delay-100">
              <div
                className={`upload-zone ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
                data-testid="upload-zone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {isLoadingPreview ? (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="font-medium">Loading preview...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Page Preview Grid */}
        {!result && pages.length > 0 && (
          <div className="space-y-6 animate-fade-in-up" data-testid="preview-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{file?.name}</h2>
                <p className="text-sm text-muted-foreground">{pages.length} pages</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => rotateAll(-90)}
                  className="rounded-sm"
                  data-testid="rotate-all-left"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  All Left
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => rotateAll(90)}
                  className="rounded-sm"
                  data-testid="rotate-all-right"
                >
                  <RotateCw className="w-4 h-4 mr-1" />
                  All Right
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {pages.map((page) => (
                <div
                  key={page.page}
                  className="border border-border hover:border-black transition-colors p-3"
                  data-testid={`page-${page.page}`}
                >
                  <div 
                    className="aspect-[3/4] bg-secondary flex items-center justify-center mb-3 overflow-hidden"
                    style={{ transform: `rotate(${rotations[page.page] || 0}deg)` }}
                  >
                    <img
                      src={`${API}/download/${page.thumbnail}`}
                      alt={`Page ${page.page}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-mono">Page {page.page}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => rotatePage(page.page, -90)}
                        className="p-1 hover:bg-secondary rounded"
                        data-testid={`rotate-left-${page.page}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => rotatePage(page.page, 90)}
                        className="p-1 hover:bg-secondary rounded"
                        data-testid={`rotate-right-${page.page}`}
                      >
                        <RotateCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {rotations[page.page] && (
                    <p className="text-xs text-[#FF3B30] mt-1">
                      Rotated {rotations[page.page]}°
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex-1 h-12 rounded-sm"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRotate}
                disabled={Object.keys(rotations).length === 0 || isProcessing}
                className="flex-1 h-12 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift"
                data-testid="apply-button"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <RotateCw className="w-5 h-5 mr-2" />
                    Apply Rotations ({Object.keys(rotations).length})
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDF Rotated Successfully
              </h2>
              <p className="text-muted-foreground mb-6">
                {result.files[0]?.filename}
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Rotate Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        <ToolContentSection toolKey="pdf-rotate" content={toolContent["pdf-rotate"]} />
        <FAQSection faqs={faqs} />
        <RelatedTools currentTool="pdf-rotate" />
      </div>
    </div>
    </>
  );
};

export default PdfRotatePage;
